import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { TableModule } from 'primeng/table';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { CashboxRecord, FinanceApiService, FinancialAccountRecord } from './finance-api.service';

interface CustomerLookup {
  displayName: string;
  code?: string;
  status?: string;
}

@Component({
  standalone: true,
  selector: 'ain-finance-cashboxes',
  imports: [AinPageComponent, TableModule, FormsModule, ButtonModule, InputTextModule, TagModule, CustomerSelectorComponent, DialogModule],
  template: `
    <ain-page [title]="t('finance.cashboxes.title')" [description]="t('finance.cashboxes.description')">
      <section class="card list-card">
        <div class="toolbar">
          <input pInputText type="text" [(ngModel)]="searchName" [placeholder]="t('finance.cashboxes.searchName')" />
          <ain-customer-selector [(ngModel)]="searchCustomerId"></ain-customer-selector>
          <button pButton type="button" [label]="t('finance.cashboxes.search')" (click)="search()"></button>
          <button pButton type="button" [label]="t('finance.cashboxes.refresh')" severity="secondary" (click)="load()"></button>
        </div>
        <div class="toolbar">
          <button pButton type="button" [label]="t('finance.cashboxes.addNew')" (click)="openCreate()"></button>
          <button pButton type="button" [label]="t('finance.cashboxes.editSelected')" severity="secondary" [disabled]="!selected()" (click)="openEditSelected()"></button>
          <button pButton type="button" [label]="t('common.delete')" severity="danger" [disabled]="!selected()" (click)="deleteSelected()"></button>
        </div>
        <p-table [value]="rows()" selectionMode="single" [selection]="selected()" (selectionChange)="onSelect($event)" dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('finance.cashboxes.fields.name') }}</th>
              <th>{{ t('finance.cashboxes.fields.customerId') }}</th>
              <th>{{ t('finance.cashboxes.fields.accountingAccountId') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.name }}</button></td>
              <td>
                <div class="customer-cell">
                  <span class="customer-name">{{ customerLabel(row.customerId) }}</span>
                  @if (customerCode(row.customerId)) {
                    <span class="customer-code">{{ customerCode(row.customerId) }}</span>
                  }
                  <p-tag [value]="customerStatusLabel(row.customerId)" [severity]="customerStatusSeverity(row.customerId)"></p-tag>
                </div>
              </td>
              <td>{{ accountLabel(row.accountingAccountId) }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
        @if (successMessage()) {
          <p class="message success">{{ successMessage() }}</p>
        }
      </section>

      <p-dialog
        [header]="t('common.delete')"
        [visible]="deleteDialogOpen()"
        [modal]="true"
        [style]="{ width: '28rem', maxWidth: '95vw' }"
        (visibleChange)="onDeleteDialogVisibleChange($event)">
        <p>{{ t('finance.cashboxes.messages.deleteConfirm') }}</p>
        <div class="dialog-footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="closeDeleteDialog()"></button>
          <button pButton type="button" severity="danger" [label]="t('common.delete')" (click)="confirmDelete()"></button>
        </div>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .message.success { color: #15803d; }
    .customer-cell { display: flex; flex-direction: column; gap: 2px; }
    .customer-name { font-weight: 600; }
    .customer-code { font-size: 12px; color: #64748b; }
    .dialog-footer { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
  `]
})
export class FinanceCashboxesComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  readonly rows = signal<CashboxRecord[]>([]);
  readonly customerById = signal<Map<string, CustomerLookup>>(new Map());
  readonly accounts = signal<FinancialAccountRecord[]>([]);
  readonly selected = signal<CashboxRecord | null>(null);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');
  readonly deleteDialogOpen = signal(false);
  searchName = '';
  searchCustomerId = '';

  ngOnInit(): void {
    this.loadReferenceData();
    this.load();
  }

  load(selectedId?: string): void {
    this.errorMessage.set('');
    this.api.listAccountingCashboxes(this.searchName, this.searchCustomerId).subscribe({
      next: data => {
        this.rows.set(data);
        const nextSelected = selectedId ? data.find(item => item.id === selectedId) ?? data[0] ?? null : data[0] ?? null;
        this.selected.set(nextSelected);
      },
      error: () => {
        this.rows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('finance.cashboxes.messages.loadFailed'));
      }
    });
  }

  onSelect(row: CashboxRecord | null): void {
    this.selected.set(row);
  }

  search(): void {
    this.load();
  }

  openCreate(): void {
    void this.router.navigate(['/financial/payments/cashboxes/new']);
  }

  openEditSelected(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.id);
  }

  deleteSelected(): void {
    if (!this.selected()) return;
    this.deleteDialogOpen.set(true);
  }

  onDeleteDialogVisibleChange(visible: boolean): void {
    if (!visible) this.closeDeleteDialog();
  }

  closeDeleteDialog(): void {
    this.deleteDialogOpen.set(false);
  }

  confirmDelete(): void {
    const row = this.selected();
    if (!row) return;

    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.deleteAccountingCashbox(row.id).subscribe({
      next: () => {
        this.closeDeleteDialog();
        this.successMessage.set(this.t('finance.cashboxes.messages.deleted'));
        this.load();
      },
      error: () => {
        this.errorMessage.set(this.t('finance.cashboxes.messages.deleteFailed'));
      }
    });
  }

  private loadReferenceData(): void {
    forkJoin({
      accountingCustomers: this.api.listAccountingCustomers().pipe(catchError(() => of([]))),
      platformCustomers: this.api.listPlatformCustomers({ take: 1000 }).pipe(catchError(() => of({ items: [], total: 0, skip: 0, take: 0 }))),
      accounts: this.api.listFinancialAccounts().pipe(catchError(() => of([])))
    }).subscribe({
      next: ({ accountingCustomers, platformCustomers, accounts }) => {
        const customerMap = new Map<string, CustomerLookup>();

        for (const customer of accountingCustomers) {
          if (!customer.key) {
            continue;
          }

          customerMap.set(customer.key.toLowerCase(), {
            displayName: customer.displayName,
            code: customer.code,
            status: customer.status
          });
        }

        for (const customer of platformCustomers.items) {
          if (!customer.id) {
            continue;
          }

          customerMap.set(customer.id.toLowerCase(), {
            displayName: customer.displayName,
            code: customer.customerCode,
            status: String(customer.status)
          });
        }

        this.customerById.set(customerMap);
        this.accounts.set(accounts);
      },
      error: () => {
        this.customerById.set(new Map());
        this.accounts.set([]);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  openDetail(id: string): void {
    void this.router.navigate(['/financial/payments/cashboxes', id]);
  }

  customerLabel(customerId?: string): string {
    const option = this.findCustomer(customerId);
    if (!option) {
      return customerId?.trim() || '-';
    }

    return option.displayName;
  }

  customerCode(customerId?: string): string {
    const option = this.findCustomer(customerId);
    return option?.code ? `#${option.code}` : '';
  }

  accountLabel(accountId?: string): string {
    if (!accountId?.trim()) {
      return '-';
    }

    const account = this.accounts().find(item => item.id.toLowerCase() === accountId.toLowerCase());
    if (!account) {
      return accountId;
    }

    return account.breadcrumb?.trim() || `${account.code} - ${account.name}`;
  }

  customerStatus(customerId?: string): 'active' | 'inactive' | 'missing' {
    const option = this.findCustomer(customerId);
    if (!option) {
      return 'missing';
    }

    const normalizedStatus = option.status?.toLowerCase() ?? '';
    if (normalizedStatus === 'active' || normalizedStatus === '1') {
      return 'active';
    }

    return 'inactive';
  }

  private findCustomer(customerId?: string): CustomerLookup | null {
    if (!customerId?.trim()) {
      return null;
    }

    return this.customerById().get(customerId.toLowerCase()) ?? null;
  }

  customerStatusLabel(customerId?: string): string {
    const status = this.customerStatus(customerId);
    if (status === 'active') {
      return this.t('payments.common.enabled');
    }
    if (status === 'inactive') {
      return this.t('payments.common.disabled');
    }
    return this.t('payments.common.missing');
  }

  customerStatusSeverity(customerId?: string): 'success' | 'warn' | 'danger' {
    const status = this.customerStatus(customerId);
    if (status === 'active') {
      return 'success';
    }
    if (status === 'inactive') {
      return 'warn';
    }
    return 'danger';
  }
}
